/* The theme's rendered cards and confirmed wishlist additions share the core normalizer. */
(() => {
  'use strict';
  if (window.IWCAnalyticsSurfaces) return;
  const diagnostics = new Set();

  function diagnostic(code) {
    if (window.IWCAnalytics?.diagnostic) return window.IWCAnalytics.diagnostic(code);
    if (diagnostics.has(code)) return;
    diagnostics.add(code);
    console.info('[IWC analytics]', code);
  }

  function cardItems(card) {
    try {
      const node = card?.querySelector('[data-iwc-card-context]');
      if (!node) return null;
      const source = JSON.parse(node.textContent);
      const fabric = source.context?.selection?.fabric_handle;
      // Wishlist hydration and colour filters can change only the image. That
      // does not establish another resolved variant, price or full selection.
      if (card.dataset.iwcContextUnresolved === 'true' ||
          (fabric && card.current_variant_value && card.current_variant_value !== fabric)) {
        source.context = null;
        source.items = source.items.map(item => ({ ...item, iwc_component_role: 'unresolved' }));
      }
      return window.IWCAnalytics?.selectionItems(source) || null;
    } catch {
      diagnostic('invalid_card_context');
      return null;
    }
  }

  function wishlistAdded(trigger) {
    try {
      const card = trigger.closest('product-card');
      let items;
      if (card) {
        items = cardItems(card);
      } else {
        const productId = trigger.dataset.wishlistProduct;
        const instance = trigger.closest('product-configurator') ||
          Array.from(document.querySelectorAll('product-configurator')).find(element =>
            String(element.config?.product?.id || '') === productId && !element.isResolving && !element.resolveError);
        items = instance ? window.IWCAnalytics?.fromConfigurator(instance)?.items : null;
      }
      if (items?.length) window.IWCAnalytics?.publish('add_to_wishlist', items);
      else diagnostic('wishlist_context_unavailable');
    } catch {
      diagnostic('wishlist_context_unavailable');
    }
  }

  window.IWCAnalyticsSurfaces = Object.freeze({ cardItems, wishlistAdded });

  function visible(card) {
    if (document.visibilityState === 'hidden') return false;
    if (!card.isConnected || card.closest('[hidden], [aria-hidden="true"]')) return false;
    if (card.checkVisibility && !card.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
    const drawer = card.closest('.drawer');
    if (drawer && !drawer.classList.contains('drawer--opened')) return false;
    const style = window.getComputedStyle(card);
    return style.visibility !== 'hidden' && style.display !== 'none' && card.getClientRects().length > 0;
  }

  function start() {
    const exposed = new WeakSet();
    const observed = new WeakSet();
    const searched = new WeakSet();
    let observer;

    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const card = entry.target;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || exposed.has(card) || !visible(card)) continue;
          const items = cardItems(card);
          if (!items?.length) continue;
          // Each genuinely exposed card is a complete batch. A component set
          // is never cut to make a payload fit; core limits reject it whole.
          exposed.add(card);
          observer.unobserve(card);
          window.IWCAnalytics?.publish('view_item_list', items);
        }
      }, { threshold: 0.5 });
    } else {
      diagnostic('exposure_observer_unavailable');
    }

    function discover(root) {
      const searches = root.matches?.('[data-iwc-search-result]') ? [root] : Array.from(root.querySelectorAll?.('[data-iwc-search-result]') || []);
      searches.forEach(result => {
        if (searched.has(result)) return;
        searched.add(result);
        window.IWCAnalytics?.publish('search', []);
      });
      if (!observer) return;
      const cards = root.matches?.('product-card') ? [root] : Array.from(root.querySelectorAll?.('product-card') || []);
      cards.forEach(card => {
        if (observed.has(card)) return;
        observed.add(card);
        observer.observe(card);
      });
    }
    discover(document);
    new MutationObserver(records => {
      records.forEach(record => {
        record.addedNodes.forEach(node => {
          if (node.nodeType === 1) discover(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    const recheck = root => root.querySelectorAll('product-card').forEach(card => {
      if (!observer || exposed.has(card)) return;
      observer.unobserve(card);
      observer.observe(card);
    });
    document.querySelectorAll('.drawer').forEach(drawer => {
      new MutationObserver(() => recheck(drawer)).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'hidden') recheck(document);
    });

    document.addEventListener('click', event => {
      try {
        if (event.defaultPrevented || event.button > 0) return;
        const link = event.target.closest('.product-card__media, .product-card__heading a, .product-card__swatches .swatch');
        const card = link?.closest('product-card');
        if (!card || card.gallery_suppress_click) return;
        const items = cardItems(card);
        if (link.matches?.('.swatch') && items?.[0]?.iwc_offer_context?.selection.fabric_handle !== link.dataset.id) {
          // A swatch can navigate to an offer whose price/variant the card has
          // not resolved. Its destination PDP owns that completed view.
          diagnostic('swatch_context_unavailable');
          return;
        }
        if (items?.length) window.IWCAnalytics?.publish('select_item', items);
      } catch {
        diagnostic('selection_context_unavailable');
      }
    });

    try {
      const market = JSON.parse(document.querySelector('[data-iwc-market]')?.textContent || '{}');
      if (market.pageType === 'search') window.IWCAnalytics?.publish('search', []);
    } catch {
      diagnostic('invalid_market_context');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
